import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid } from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

/**
 * PointsHistoryChart — לוח התקדמות נקודות אישי בפרופיל
 * Props: userId (string)
 */
const PointsHistoryChart = ({ userId }) => {
  const API = process.env.REACT_APP_BACKEND_URL + '/api';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    axios.get(`${API}/users/${userId}/points-history`)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId, API]);

  if (loading) return null;
  if (!data) return null;

  const history = data.history || [];

  // Build chart points: start with 0 points at account creation, then each history entry
  const chartData = [];
  if (data.user_created_at) {
    chartData.push({
      date: new Date(data.user_created_at).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: '2-digit' }),
      timestamp: new Date(data.user_created_at).getTime(),
      points: 0,
      delta: null,
      reason: 'הצטרפות לאתר',
    });
  }
  history.forEach(h => {
    chartData.push({
      date: new Date(h.created_at).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: '2-digit' }),
      timestamp: new Date(h.created_at).getTime(),
      points: h.points_after,
      delta: h.delta,
      reason: h.reason,
      admin: h.admin_name,
    });
  });

  // Always end with current points
  if (chartData.length === 0 || chartData[chartData.length - 1].points !== data.current_points) {
    chartData.push({
      date: new Date().toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: '2-digit' }),
      timestamp: Date.now(),
      points: data.current_points,
      delta: null,
      reason: 'נכון להיום',
    });
  }

  if (chartData.length < 2) {
    return (
      <Card className="mt-4" data-testid="points-history-chart-empty">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">📈 גרף התקדמות נקודות</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 text-center py-6">
            עדיין אין מספיק נתונים. הגרף יופיע אחרי שתקבל את העדכון הראשון של נקודות 🎾
          </p>
        </CardContent>
      </Card>
    );
  }

  const firstPoints = chartData[0].points;
  const lastPoints = chartData[chartData.length - 1].points;
  const totalChange = lastPoints - firstPoints;
  const maxPoints = Math.max(...chartData.map(d => d.points));

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs" dir="rtl">
        <div className="font-bold text-slate-900">{p.date}</div>
        <div className="text-emerald-700 font-semibold mt-1">💎 {p.points} נק׳</div>
        {p.delta !== null && p.delta !== undefined && (
          <div className={`font-bold mt-1 ${p.delta > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {p.delta > 0 ? '+' : ''}{p.delta} שינוי
          </div>
        )}
        {p.reason && <div className="text-slate-500 mt-1">{p.reason}</div>}
        {p.admin && <div className="text-slate-400 text-[10px] mt-0.5">עודכן ע״י: {p.admin}</div>}
      </div>
    );
  };

  return (
    <Card className="mt-4" data-testid="points-history-chart">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            📈 גרף התקדמות נקודות
          </CardTitle>
          <div className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full ${
            totalChange > 0 ? 'bg-emerald-100 text-emerald-800' :
            totalChange < 0 ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-700'
          }`}>
            {totalChange > 0 ? <TrendingUp className="w-3 h-3" /> : totalChange < 0 ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
            <span>{totalChange > 0 ? '+' : ''}{totalChange} נק׳ סך הכל</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="pointsGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#10b981" />
                  <stop offset="100%" stopColor="#3b82f6" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} domain={[0, Math.ceil(maxPoints * 1.1)]} />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={firstPoints} stroke="#94a3b8" strokeDasharray="3 3" label={{ value: 'התחלה', position: 'insideTopLeft', fontSize: 10, fill: '#94a3b8' }} />
              <Line
                type="monotone"
                dataKey="points"
                stroke="url(#pointsGrad)"
                strokeWidth={3}
                dot={{ r: 4, fill: '#10b981', stroke: '#fff', strokeWidth: 2 }}
                activeDot={{ r: 6, fill: '#10b981', stroke: '#fff', strokeWidth: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Last 5 changes list */}
        {history.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="text-xs font-bold text-slate-600 mb-2">📋 שינויים אחרונים</div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {[...history].reverse().slice(0, 5).map(h => (
                <div key={h.id} className="flex items-center justify-between gap-2 text-xs p-2 bg-slate-50 rounded">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-700 truncate">{h.reason || 'עדכון נקודות'}</div>
                    <div className="text-[10px] text-slate-400">
                      {new Date(h.created_at).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      {h.admin_name && ` · ${h.admin_name}`}
                    </div>
                  </div>
                  <div className={`font-bold px-2 py-0.5 rounded ${
                    h.delta > 0 ? 'bg-emerald-100 text-emerald-700' :
                    h.delta < 0 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'
                  }`}>
                    {h.delta > 0 ? '+' : ''}{h.delta}
                  </div>
                  <div className="text-slate-500 text-[10px] tabular-nums">{h.points_before}→{h.points_after}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PointsHistoryChart;
